import { useCallback, useEffect, useMemo, useState } from "react";
import { autoOrient } from "../services/imageOrientation";

export interface UploadedFile {
  file: File;
  imageBase64: string;
  /** Object URL for the in-page preview. */
  previewUrl: string;
}

export interface UseImageUploadResult {
  files: UploadedFile[];
  /** Cap enforced when accepting new files (input / drop / paste). */
  maxFiles: number;
  /** Append one or more files to the current list (capped at maxFiles). */
  add: (incoming: FileList | File[] | null | undefined) => void;
  reset: () => void;
  removeAt: (index: number) => void;
}

/** Default cap on number of images per upload batch. */
export const DEFAULT_MAX_FILES = 8;

/** Take an incoming list (from an input, drop, or paste) and keep only image
 *  files. Returns the cleaned list, never null. */
function pickImages(incoming: FileList | File[] | null | undefined): File[] {
  if (!incoming) return [];
  return Array.from(incoming).filter((f) => f.type.startsWith("image/"));
}

/**
 * Read a single File into { previewUrl, imageBase64 }. Uses
 * `services/imageOrientation::autoOrient` to apply EXIF orientation so the
 * bytes we send to the Worker are upright (Workers AI's vision model sees the
 * image the same way the user does).
 */
async function readFile(file: File): Promise<UploadedFile> {
  const previewUrl = URL.createObjectURL(file);
  try {
    const oriented = await autoOrient(file);
    return {
      file,
      previewUrl,
      imageBase64: oriented.base64,
    };
  } catch (err) {
    URL.revokeObjectURL(previewUrl);
    throw err instanceof Error ? err : new Error("无法读取文件");
  }
}

/**
 * Manage one or more uploaded images. Keeps each File, an Object-URL preview,
 * and the raw base64 payload (no data URL prefix) ready for the Worker.
 *
 * New files are appended to the existing list; if the resulting count would
 * exceed `maxFiles`, the trailing extras are dropped (with a console.warn
 * so it's visible during development). Callers can set a custom cap via
 * the optional `maxFiles` argument.
 */
export function useImageUpload(maxFiles: number = DEFAULT_MAX_FILES): UseImageUploadResult {
  const [files, setFiles] = useState<UploadedFile[]>([]);

  // Revoke any preview URLs when the component unmounts so we don't leak
  // memory between navigations.
  useEffect(() => {
    return () => {
      for (const f of files) {
        URL.revokeObjectURL(f.previewUrl);
      }
    };
    // We only want this on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = useCallback(
    async (incoming: FileList | File[] | null | undefined) => {
      const picked = pickImages(incoming);
      if (picked.length === 0) return;
      setFiles((prev) => {
        const remaining = Math.max(0, maxFiles - prev.length);
        if (remaining === 0) {
          // eslint-disable-next-line no-console
          console.warn(`useImageUpload: 已达上限 ${maxFiles} 张，跳过新文件`);
          return prev;
        }
        const accepted = picked.slice(0, remaining);
        if (picked.length > accepted.length) {
          // eslint-disable-next-line no-console
          console.warn(
            `useImageUpload: 仅接受前 ${accepted.length} 张（上限 ${maxFiles}）`,
          );
        }
        // Kick off the readers; they each resolve and we mutate state once
        // per completion. Using a closure over `prev.length` keeps each
        // accepted file queued against the snapshot we just decided on.
        accepted.forEach((file) => {
          readFile(file)
            .then((uploaded) => {
              setFiles((cur) => {
                if (cur.length >= maxFiles) {
                  // Lost a race with another add(); drop the new one.
                  URL.revokeObjectURL(uploaded.previewUrl);
                  return cur;
                }
                return [...cur, uploaded];
              });
            })
            .catch(() => {
              // Swallow — previewUrl already revoked in readFile on error.
            });
        });
        return prev;
      });
    },
    [maxFiles],
  );

  const reset = useCallback(() => {
    setFiles((cur) => {
      for (const f of cur) {
        URL.revokeObjectURL(f.previewUrl);
      }
      return [];
    });
  }, []);

  const removeAt = useCallback((index: number) => {
    setFiles((cur) => {
      if (index < 0 || index >= cur.length) return cur;
      const next = cur.slice();
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  }, []);

  const value = useMemo<UseImageUploadResult>(
    () => ({ files, maxFiles, add, reset, removeAt }),
    [files, maxFiles, add, reset, removeAt],
  );

  return value;
}
